/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';

import Button from 'components/base/Button';
import activityService from 'service/activity';
import countryService from 'service/country';
import currencyService from 'service/currency';
import areaService from 'service/district';
import regionService from 'service/region';
import legalEntityStatusService from 'service/legalEntityStatus';
import PhoneNumberInput from 'components/forms/PhoneNumberInput';
import useCountryUnicode from 'hooks/useCountryUnicode';
import legalEntityService from 'service/legalEntity';
import { showAlert } from 'store/alert/alert.thunk';
import accessesService from 'service/legalEntityAccess';
import ImageUpload from 'components/common/ImageUpload';

import './styles.scss';

interface CreateLegalEntityProps {
  setOpenLegalCreate: (show: boolean) => void;
  selectedLegalId?: string;
  refetch: () => void;
  setSelectedLegalId: (id: string) => void;
}

interface Option {
  value: string;
  label: string;
}

const CreateLegalEntity = ({
  setOpenLegalCreate,
  selectedLegalId,
  refetch,
  setSelectedLegalId
}: CreateLegalEntityProps) => {
  const { t, i18n } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors }
  } = useForm();
  const unicode = useCountryUnicode();
  const dispatch: Dispatch<any> = useDispatch();
  const hasFetchedData = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const system_level = useSelector(
    (state: any) => state?.auth?.user?.role?.system_level
  );

  const [loading, setLoading] = useState(false);

  const [countryOption, setCountryOption] = useState<Option[]>([]);
  const [activityOption, setActivityOption] = useState<Option[]>([]);
  const [currencyOption, setCurrencyOption] = useState<Option[]>([]);
  const [areaOption, setAreaOption] = useState<Option[]>([]);
  const [regionOption, setRegionOption] = useState<Option[]>([]);
  const [legalStatusOption, setLegalStatusOption] = useState<Option[]>([]);
  const [legalAccessOption, setLegalAccessOption] = useState<Option[]>([]);
  const [typeOption, setTypeOption] = useState<Option[]>([]);
  const [mapCenter, setMapCenter] = useState([41.2995, 69.2401]); // Default to Tashkent, Uzbekistan
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(
    null
  );

  const mapStyles = {
    height: '200px',
    width: '100%'
  };

  const fetchOptions = async (
    service: any,
    key: string,
    setter: (options: Option[]) => void
  ) => {
    const res = await service.getList({ offset: 0, limit: 100, status: true });
    const options = res[key]?.map((item: any) => ({
      value: item.id,
      label: item.name?.[i18n.language]
    }));
    setter(options);
  };

  useQuery(['GET_ACTIVITY_KIND'], () =>
    fetchOptions(activityService, 'activity_kinds', setActivityOption)
  );
  useQuery(['GET_COUNTRY'], () =>
    fetchOptions(countryService, 'countries', setCountryOption)
  );
  useQuery(['GET_CURRENCY'], () =>
    fetchOptions(currencyService, 'currencies', setCurrencyOption)
  );
  useQuery(['GET_AREA'], () =>
    fetchOptions(areaService, 'districts', setAreaOption)
  );
  useQuery(['GET_REGION'], () =>
    fetchOptions(regionService, 'regions', setRegionOption)
  );
  useQuery(['GET_LEGAL_ENTITY_STATUS'], () =>
    fetchOptions(
      legalEntityStatusService,
      'legal_entity_statuses',
      setLegalStatusOption
    )
  );
  useQuery(['GET_ACCESS'], () =>
    fetchOptions(accessesService, 'legal_entity_accesses', setLegalAccessOption)
  );

  const fetchData = async () => {
    if (!selectedLegalId) return;

    const resData = await legalEntityService
      .getLegalEntityById(selectedLegalId)
      .then((res: any) => {
        const computed = {
          physical_name: res?.name,
          address: res?.address,
          phone: res?.phone_number,
          type_physical_name: res.legal_entity_type,
          kinds: res?.activity_kind_id,
          country: res?.country_id,
          def_currency: res?.currency?.id,
          area: res?.district_id,
          region: res?.region_id,
          access: res?.legal_entity_access_id,
          status: res?.legal_entity_status_id,
          type_interaction: res?.legal_entity_access.id,
          logo_url: res.logo_url
            ? process.env.REACT_APP_CDN_URL + res.logo_url
            : null,
          photo_url: res.photo_url
            ? process.env.REACT_APP_CDN_URL + res.photo_url
            : null,
          l_long: res?.l_long,
          l_lat: res?.l_lat
        };

        if (res?.l_lat && res?.l_long) {
          setMapCenter([parseFloat(res.l_lat), parseFloat(res.l_long)]);
          setMarkerPosition([parseFloat(res.l_lat), parseFloat(res.l_long)]);
        }

        return computed;
      });

    if (resData) {
      reset(resData);
    }
  };

  const handleMapClick = (event: any) => {
    const coords = event.get('coords');
    if (coords) {
      setMarkerPosition(coords);
      setMapCenter(coords);
      setValue('l_lat', coords[0].toString(), { shouldValidate: true });
      setValue('l_long', coords[1].toString(), { shouldValidate: true });
    }
  };

  useEffect(() => {
    if (selectedLegalId !== '' && !hasFetchedData.current) {
      fetchData();
      hasFetchedData.current = true;
    }
  }, [selectedLegalId]);

  const onSubmit: SubmitHandler<FieldValues> = data => {
    if (selectedLegalId !== '') return update(data);
    createData(data);
  };

  const createData = (data: any) => {
    setLoading(true);

    let l_long = null;
    let l_lat = null;

    // Check if the data contains a link for `l_long`
    if (data?.l_long) {
      try {
        if (data.l_long.includes('http')) {
          const longUrl = new URL(data.l_long);

          // Parse Yandex Maps link
          if (longUrl.hostname.includes('yandex')) {
            const llParam = longUrl.searchParams.get('ll');
            if (llParam) {
              [l_long, l_lat] = llParam
                .split(',')
                .map(coord => parseFloat(coord));
            }
          }

          // Parse Google Maps link
          if (longUrl.hostname.includes('google')) {
            const coordsMatch = longUrl.pathname.match(/@([\d.-]+),([\d.-]+)/);
            if (coordsMatch) {
              [, l_lat, l_long] = coordsMatch.map(coord => parseFloat(coord));
            }
          }
        } else {
          // If it's not a URL, use the raw value
          l_long = parseFloat(data.l_long);
        }
      } catch (error) {
        console.error('Error parsing map link:', error);
        // Fallback to raw value if parsing fails
        l_long = parseFloat(data.l_long);
      }
    }

    // Check if the data contains a link for `l_lat`
    if (data?.l_lat) {
      try {
        if (data.l_lat.includes('http')) {
          const latUrl = new URL(data.l_lat);

          // Parse Yandex Maps link for latitude
          if (latUrl.hostname.includes('yandex')) {
            const llParam = latUrl.searchParams.get('ll');
            if (llParam) {
              [l_long, l_lat] = llParam
                .split(',')
                .map(coord => parseFloat(coord));
            }
          }

          // Parse Google Maps link for latitude
          if (latUrl.hostname.includes('google')) {
            const coordsMatch = latUrl.pathname.match(/@([\d.-]+),([\d.-]+)/);
            if (coordsMatch) {
              [, l_lat, l_long] = coordsMatch.map(coord => parseFloat(coord));
            }
          }
        } else {
          // If it's not a URL, use the raw value
          l_lat = parseFloat(data.l_lat);
        }
      } catch (error) {
        console.error('Error parsing map link:', error);
        // Fallback to raw value if parsing fails
        l_lat = parseFloat(data.l_lat);
      }
    }

    // If `l_lat` is not available, fallback to `lat` in `data`
    l_lat = l_lat ?? parseFloat(data?.lat);

    // If `l_long` is not available, fallback to `long` in `data`
    l_long = l_long ?? parseFloat(data?.long);

    const createdData = {
      name: data?.physical_name,
      activity_kind_id: data?.kinds,
      address: data?.address,
      country_id: data?.country,
      currency_id: data?.def_currency,
      district_id: data?.area,
      legal_entity_status_id: data?.status,
      legal_entity_access_id: data?.type_interaction,
      legal_entity_type: data?.type_physical_name,
      phone_number: data?.phone,
      region_id: data?.region,
      logo_url: data?.logo_url
        ? data.logo_url.substring(data.logo_url.indexOf('/photos/'))
        : '',
      photo_url: data?.photo_url
        ? data.photo_url.substring(data.photo_url.indexOf('/photos/'))
        : '',
      l_long: l_long ?? data?.l_long,
      l_lat: l_lat ?? data?.l_lat
    };

    legalEntityService
      .createLegalEntity(createdData)
      .then((res: any) => {
        if (res) {
          dispatch(
            showAlert({
              title: `${t('legal_entities')} ${t('successfully_added')}`,
              type: 'success'
            })
          );
        }
      })
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => {
        refetch();
        setOpenLegalCreate(false);
        setLoading(false);
        setSelectedLegalId('');
      });
  };

  const update = (data: any) => {
    setLoading(true);

    let l_long = null;
    let l_lat = null;

    // Check if the data contains a link for `l_long`
    if (data?.l_long) {
      try {
        if (data.l_long.includes('http')) {
          const longUrl = new URL(data.l_long);

          // Parse Yandex Maps link
          if (longUrl.hostname.includes('yandex')) {
            const llParam = longUrl.searchParams.get('ll');
            if (llParam) {
              [l_long, l_lat] = llParam
                .split(',')
                .map(coord => parseFloat(coord));
            }
          }

          // Parse Google Maps link
          if (longUrl.hostname.includes('google')) {
            const coordsMatch = longUrl.pathname.match(/@([\d.-]+),([\d.-]+)/);
            if (coordsMatch) {
              [, l_lat, l_long] = coordsMatch.map(coord => parseFloat(coord));
            }
          }
        } else {
          // If it's not a URL, use the raw value
          l_long = parseFloat(data.l_long);
        }
      } catch (error) {
        console.error('Error parsing map link:', error);
        // Fallback to raw value if parsing fails
        l_long = parseFloat(data.l_long);
      }
    }

    // Check if the data contains a link for `l_lat`
    if (data?.l_lat) {
      try {
        if (data.l_lat.includes('http')) {
          const latUrl = new URL(data.l_lat);

          // Parse Yandex Maps link for latitude
          if (latUrl.hostname.includes('yandex')) {
            const llParam = latUrl.searchParams.get('ll');
            if (llParam) {
              [l_long, l_lat] = llParam
                .split(',')
                .map(coord => parseFloat(coord));
            }
          }

          // Parse Google Maps link for latitude
          if (latUrl.hostname.includes('google')) {
            const coordsMatch = latUrl.pathname.match(/@([\d.-]+),([\d.-]+)/);
            if (coordsMatch) {
              [, l_lat, l_long] = coordsMatch.map(coord => parseFloat(coord));
            }
          }
        } else {
          // If it's not a URL, use the raw value
          l_lat = parseFloat(data.l_lat);
        }
      } catch (error) {
        console.error('Error parsing map link:', error);
        // Fallback to raw value if parsing fails
        l_lat = parseFloat(data.l_lat);
      }
    }

    // If `l_lat` is not available, fallback to `lat` in `data`
    l_lat = l_lat ?? parseFloat(data?.lat);

    // If `l_long` is not available, fallback to `long` in `data`
    l_long = l_long ?? parseFloat(data?.long);

    const updateData = {
      name: data?.physical_name,
      activity_kind_id: data?.kinds,
      address: data?.address,
      country_id: data?.country,
      currency_id: data?.def_currency,
      district_id: data?.area,
      legal_entity_access_id: data?.type_interaction,
      legal_entity_status_id: data?.status,
      legal_entity_type: data?.type_physical_name,
      phone_number: data?.phone,
      region_id: data?.region,
      logo_url: data?.logo_url
        ? data.logo_url.substring(data.logo_url.indexOf('/photos/'))
        : '',
      photo_url: data?.photo_url
        ? data.photo_url.substring(data.photo_url.indexOf('/photos/'))
        : '',
      id: selectedLegalId,
      l_long: l_long ?? data?.l_long,
      l_lat: l_lat ?? data?.l_lat
    };
    legalEntityService
      .updateLegalEntity(selectedLegalId, updateData)
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => {
        refetch();
        setLoading(false);
        setOpenLegalCreate(false);
        setSelectedLegalId('');
      });
  };

  useEffect(() => {
    const typeOptions: Record<
      'ROOT' | 'HOLDING' | 'BRAND' | 'DEALER' | 'STORE',
      string[]
    > = {
      ROOT: ['HOLDING', 'BRAND', 'DEALER', 'STORE'],
      HOLDING: ['BRAND', 'DEALER', 'STORE'],
      BRAND: ['DEALER', 'STORE'],
      DEALER: ['STORE'],
      STORE: ['STORE']
    };

    const options =
      typeOptions[system_level as keyof typeof typeOptions]?.map(value => ({
        value,
        label: t(value.toLowerCase())
      })) || [];

    setTypeOption(options);
  }, [system_level, t]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => inputRef.current?.select(), 500);
    }
  }, []);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} className="form-cls">
        <Row className="mb-2">
          <Col>
            <Controller
              name="physical_name"
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating className="md-4">
                  <Form.Control
                    {...field}
                    ref={inputRef}
                    type="text"
                    placeholder="Physical name"
                    onFocus={e => e.target.select()}
                    isInvalid={!!errors.physical_name}
                  />
                  <label htmlFor="floatingInputCustom">
                    {t('name_legal_entity')}
                  </label>
                  {errors?.physical_name && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Controller
              name="type_physical_name"
              control={control}
              defaultValue=""
              rules={{
                required: true
              }}
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select
                    {...field}
                    isInvalid={!!errors.type_physical_name}
                  >
                    {!field.value && <option value="" disabled hidden></option>}
                    {typeOption?.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">
                    {t('legal_entity_type')}
                  </label>

                  {errors?.type_physical_name && (
                    <Form.Control.Feedback type="invalid">
                      {t('required_field')}
                    </Form.Control.Feedback>
                  )}
                </Form.Floating>
              )}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="type_interaction"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field}>
                      <option className="d-none" value=""></option>
                      {legalAccessOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('interaction_type')}
                    </label>
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="kinds"
                control={control}
                defaultValue=""
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field} isInvalid={!!errors.kinds}>
                      <option className="d-none" value=""></option>
                      {activityOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('activity_type')}
                    </label>
                    {errors?.kinds && (
                      <Form.Control.Feedback type="invalid">
                        {t('required_field')}
                      </Form.Control.Feedback>
                    )}
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="def_currency"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field}>
                      <option className="d-none" value=""></option>
                      {currencyOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">
                      {t('default_currency')}
                    </label>
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="status"
                control={control}
                defaultValue=""
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field} isInvalid={!!errors.status}>
                      <option className="d-none" value=""></option>
                      {legalStatusOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">{t('status')}</label>
                    {errors?.status && (
                      <Form.Control.Feedback type="invalid">
                        {t('required_field')}
                      </Form.Control.Feedback>
                    )}
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="country"
                control={control}
                defaultValue=""
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field} isInvalid={!!errors.country}>
                      <option className="d-none" value=""></option>
                      {countryOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">{t('country')}</label>
                    {errors?.country && (
                      <Form.Control.Feedback type="invalid">
                        {t('required_field')}
                      </Form.Control.Feedback>
                    )}
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="region"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field}>
                      <option className="d-none" value=""></option>
                      {regionOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">{t('region')}</label>
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Controller
                name="area"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Form.Floating>
                    <Form.Select {...field}>
                      <option className="d-none" value=""></option>
                      {areaOption?.map((option: any) => (
                        <option
                          key={option.value}
                          value={option.value}
                          className="option"
                        >
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>

                    <label htmlFor="floatingInputCustom">{t('area')}</label>
                  </Form.Floating>
                )}
              />
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('address')}
                type="text"
                placeholder="address"
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('address')}</label>
            </Form.Floating>
          </Col>
        </Row>
        <Row className="mb-3 d-flex gap-2">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('l_long')}
                type="text"
                placeholder={t('l_long')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('l_long')}</label>
            </Form.Floating>
          </Col>
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('l_lat')}
                type="text"
                placeholder={t('l_lat')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('l_lat')}</label>
            </Form.Floating>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <YMaps
              query={{
                lang: 'ru_RU',
                load: 'package.full'
              }}
            >
              <Map
                defaultState={{ center: mapCenter, zoom: 13 }}
                width={mapStyles.width}
                height={mapStyles.height}
                onClick={handleMapClick}
              >
                {markerPosition && <Placemark geometry={markerPosition} />}
              </Map>
            </YMaps>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <PhoneNumberInput field={field} unicode={unicode} />
              )}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Form.Label>{t('logo')}</Form.Label>
            <Controller
              name="logo_url"
              control={control}
              defaultValue=""
              render={({ field }) => <ImageUpload {...field} />}
            />
            <div className="foto-container">
              <p>{t('file_size')}</p>
              <p>{t('image_in_48')}</p>
            </div>
          </Col>

          <Col>
            <Form.Label>{t('photo')}</Form.Label>
            <Controller
              name="photo_url"
              control={control}
              defaultValue=""
              render={({ field }) => <ImageUpload {...field} />}
            />
            <div className="foto-container">
              <p>{t('file_size')}</p>
              <p>{t('image_in_343')}</p>
            </div>
          </Col>
        </Row>

        <Button
          style={{ height: '48px', marginTop: 'auto' }}
          variant="primary"
          type="submit"
          loading={loading}
          loadingPosition="start"
        >
          {t('add')}
        </Button>
      </Form>
    </>
  );
};

export default CreateLegalEntity;
